import { useGetSubscriptionsV1SubscriptionsGet } from '@/api/generated-catch-backend/subscriptions/subscriptions';
import authService from '@/services/auth.service';
import { hasWorkosSession } from '@/services/workos-auth-bridge';

const ACTIVE_STATUSES = new Set(['active', 'trialing']);

// Minimal shape required from the /me response. Compatible with both the context
// repository and the generated member-backend client (whose membership union
// includes a "not-active" variant that omits `type`) so callers inside and
// outside MeProvider can use this hook.
type MembershipMeData = {
  membership?: { type?: string | null; status?: string | null } | null;
} | null | undefined;

export interface HasActiveMembershipResult {
  isActive: boolean;
  isVip: boolean;
  stripeSubscriptionId: string | null;
  isLoading: boolean;
}

/**
 * Merges catch-backend's Stripe subscription status with member-backend's
 * VIP signal into a single "does the user have access" result.
 *
 * Each backend still owns its own slice of state: catch-backend is the source
 * of truth for subscriptions, member-backend for VIP. This merge lives in the
 * UI until VIP moves into catch-backend.
 */
export function useHasActiveMembership(me: MembershipMeData): HasActiveMembershipResult {
  // Only query subscriptions when the user is authenticated. An unauthenticated
  // visitor (e.g. on /payment in incognito) has no access by definition, and
  // the global axios interceptor would otherwise log them out and redirect to
  // /sign-in on the resulting 401 — which silently breaks the signup flow.
  // WorkOS-authenticated users have no legacy session-token, so check both.
  const isLoggedIn = authService.isLoggedIn() || hasWorkosSession();
  const { data: subscription, isLoading: isSubLoading } = useGetSubscriptionsV1SubscriptionsGet({
    query: { retry: false, enabled: isLoggedIn },
  });

  const isVip = me?.membership?.type === 'VIP';
  const status = subscription?.status?.toLowerCase();
  const hasActiveSubscription = !!status && ACTIVE_STATUSES.has(status);

  return {
    isActive: hasActiveSubscription || isVip,
    isVip,
    stripeSubscriptionId: subscription?.id ?? null,
    isLoading: isSubLoading,
  };
}
